import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Eye, LogIn, Users, TrendingUp, ArrowRight } from "lucide-react";

/**
 * Phase 4.2 — Portal Conversion Funnel.
 * Drei Stufen: Portal-Aufrufe → Logins → Aktive Solar-Kunden.
 *
 * Daten: GET /api/agency/portal-funnel
 */
const STAGE_META = [
  { key: "views", icon: Eye, color: "#7c3aed", subtle: "#ede9fe" },
  { key: "logins", icon: LogIn, color: "#0ea5e9", subtle: "#e0f2fe" },
  { key: "active_clients", icon: Users, color: "#10b981", subtle: "#d1fae5" },
];

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
  } catch { return null; }
}

export default function PortalFunnelCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/agency/portal-funnel");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) return null; // Funnel ist optional, kein Crash
  if (!data) {
    return (
      <Card className="p-6 border-slate-200" data-testid="portal-funnel-card-loading">
        <p className="text-sm text-slate-500">Funnel wird geladen …</p>
      </Card>
    );
  }

  const stages = data.stages || [];
  const maxVal = Math.max(1, ...stages.map(s => s.value));
  const conv = data.conversion || {};

  return (
    <Card className="p-6 border-slate-200 space-y-5" data-testid="portal-funnel-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-sky-500 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Portal-Conversion-Funnel</h2>
            <p className="text-xs text-slate-500">Aufrufe → Logins → aktive Solar-Kunden</p>
          </div>
        </div>
        {stages[0]?.value > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-50 text-violet-700 font-semibold tabular-nums" data-testid="funnel-conv-view-to-active">
              {conv.view_to_active_pct}% End-to-End
            </span>
          </div>
        )}
      </div>

      {/* Horizontal bars + conversion arrows */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-center">
        {stages.map((s, idx) => {
          const meta = STAGE_META[idx] || STAGE_META[0];
          const Icon = meta.icon;
          const width = (s.value / maxVal) * 100;
          const lastDate = formatDate(s.subtitle);
          return (
            <React.Fragment key={s.key}>
              <div
                data-testid={`funnel-stage-${s.key}`}
                className="rounded-lg p-3.5 border"
                style={{ background: meta.subtle, borderColor: `${meta.color}33` }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
                    Stufe {idx + 1}
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-medium">{s.label}</p>
                <p className="text-3xl font-extrabold text-slate-900 tabular-nums mt-1" style={{ fontFamily: "Manrope, sans-serif" }} data-testid={`funnel-value-${s.key}`}>
                  {s.value.toLocaleString("de-DE")}
                </p>
                <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${width}%`, background: meta.color }}
                  />
                </div>
                {lastDate && (
                  <p className="text-[10px] text-slate-500 mt-1.5">zuletzt {lastDate}</p>
                )}
              </div>
              {idx < stages.length - 1 && (
                <div className="flex md:flex-col items-center justify-center gap-1 px-1" data-testid={`funnel-arrow-${idx}`}>
                  <ArrowRight className="w-4 h-4 text-slate-400 hidden md:block" />
                  <ArrowRight className="w-4 h-4 text-slate-400 rotate-90 md:hidden" />
                  <span className="text-[11px] font-bold text-slate-600 tabular-nums">
                    {idx === 0 ? `${conv.view_to_login_pct}%` : `${conv.login_to_active_pct}%`}
                  </span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {stages[0]?.value === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600" data-testid="funnel-empty-hint">
          Sobald Sie Ihren Portal-Link teilen, sehen Sie hier den Funnel von Aufruf bis aktivem Solar-Kunden.
          Tipp: Den Link aus der &bdquo;Magic Link&ldquo;-Karte oben kopieren und in Ihre nächste Akquise-Sequenz einbauen.
        </div>
      )}
    </Card>
  );
}
